import { reportError } from "@/utils/reportError";
import { trackRankingConsent } from "@/utils/analytics";
import { showAlert } from "@/utils/showAlert";
import { supabase } from "@/utils/supabase";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { Stack, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ActivityIndicator,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from "react-native";

export default function RankingConsentScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const saveConsent = async (accept: boolean) => {
    trackRankingConsent(accept);
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        // Sans session, le consentement ne peut pas être sauvé : ne pas entrer
        // dans l'app (sinon display_main_ranking reste null → on reviendrait ici
        // à chaque ouverture). On renvoie vers le login.
        showAlert(
          t("rankingConsent.sessionExpiredTitle"),
          t("rankingConsent.sessionExpiredMessage"),
          [{ text: t("common.ok"), onPress: () => router.replace("/login") }]
        );
        return;
      }
      const { error } = await supabase
        .from('profiles')
        .update({ display_main_ranking: accept })
        .eq('id', user.id);
      if (error) {
        // Échec d'écriture : on reste sur l'écran pour réessayer plutôt que de
        // boucler indéfiniment au prochain lancement.
        reportError(error);
        showAlert(
          t("common.errorTitle"),
          t("rankingConsent.saveErrorMessage")
        );
        return;
      }
      router.replace("/(tabs)");
    } catch (error) {
      reportError(error);
      showAlert(
        t("common.errorTitle"),
        t("rankingConsent.saveErrorMessage")
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false }} />
      <StatusBar style="light" />
      <LinearGradient
        colors={["#CD7728", "#FCC189"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={styles.background}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.iconContainer}>
            <View style={styles.iconCircle}>
              <Ionicons name="trophy" size={64} color="#CD7728" />
            </View>
          </View>

          <Text style={styles.title}>{t("rankingConsent.title")}</Text>

          <Text style={styles.subtitle}>{t("rankingConsent.subtitle")}</Text>

          <View style={styles.benefitsContainer}>
            <BenefitItem
              icon="podium-outline"
              title={t("rankingConsent.benefitProgressTitle")}
              description={t("rankingConsent.benefitProgressDescription")}
            />
            <BenefitItem
              icon="people-outline"
              title={t("rankingConsent.benefitMotivationTitle")}
              description={t("rankingConsent.benefitMotivationDescription")}
            />
            <BenefitItem
              icon="shield-checkmark-outline"
              title={t("rankingConsent.benefitAnonymousTitle")}
              description={t("rankingConsent.benefitAnonymousDescription")}
            />
          </View>

          <View style={styles.privacyContainer}>
            <Ionicons name="eye-off-outline" size={20} color="#FFF" />
            <Text style={styles.privacyText}>
              {t("rankingConsent.privacyNote")}
            </Text>
          </View>
        </ScrollView>

        <View style={styles.buttonsContainer}>
          <TouchableOpacity
            onPress={() => saveConsent(true)}
            disabled={isLoading}
            style={styles.primaryButton}
            activeOpacity={0.8}
          >
            {isLoading ? (
              <ActivityIndicator color="#CD7728" />
            ) : (
              <Text style={styles.primaryButtonText}>
                {t("rankingConsent.acceptButton")}
              </Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => saveConsent(false)}
            style={styles.skipButton}
            disabled={isLoading}
          >
            <Text style={styles.skipButtonText}>{t("rankingConsent.declineButton")}</Text>
          </TouchableOpacity>
        </View>
      </LinearGradient>
    </View>
  );
}

interface BenefitItemProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
}

function BenefitItem({ icon, title, description }: BenefitItemProps) {
  return (
    <View style={styles.benefitItem}>
      <View style={styles.benefitIconContainer}>
        <Ionicons name={icon} size={24} color="#CD7728" />
      </View>
      <View style={styles.benefitTextContainer}>
        <Text style={styles.benefitTitle}>{title}</Text>
        <Text style={styles.benefitDescription}>{description}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 60,
    paddingBottom: 20,
  },
  iconContainer: {
    alignItems: "center",
    marginBottom: 32,
    marginTop: 20,
  },
  iconCircle: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: "#FFF",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 32,
    color: "#FFF",
    textAlign: "center",
    marginBottom: 16,
    lineHeight: 40,
  },
  subtitle: {
    fontFamily: "Inter_400Regular",
    fontSize: 17,
    color: "rgba(255, 255, 255, 0.9)",
    textAlign: "center",
    lineHeight: 24,
    marginBottom: 40,
    paddingHorizontal: 10,
  },
  benefitsContainer: {
    marginBottom: 32,
  },
  benefitItem: {
    flexDirection: "row",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  benefitIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#FFF9F0",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
  },
  benefitTextContainer: {
    flex: 1,
    justifyContent: "center",
  },
  benefitTitle: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 16,
    color: "#1C453F",
    marginBottom: 4,
  },
  benefitDescription: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    lineHeight: 20,
  },
  privacyContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderRadius: 12,
    padding: 16,
    marginTop: 8,
  },
  privacyText: {
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#FFF",
    flex: 1,
    marginLeft: 12,
    lineHeight: 18,
  },
  buttonsContainer: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    paddingTop: 20,
  },
  primaryButton: {
    backgroundColor: "#FFF",
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: "center",
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  primaryButtonText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#CD7728",
  },
  skipButton: {
    paddingVertical: 16,
    alignItems: "center",
  },
  skipButtonText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 16,
    color: "rgba(255, 255, 255, 0.9)",
  },
});
